import type { NegotiationFormValues } from "@/components/Forms/NegotiationForm.tsx";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { invalidateCaseQueries } from "@/utils/cacheUtils.ts";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { handleJsonApiError } from "jsonapi-zod-query";

export const useSaveCaseNegotiationMutation = (
    caseUUID: string,
): UseMutationResult<void, Error, NegotiationFormValues> => {
    const fetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (attributes) => {
            const response = await fetch(apiUrl(`/negotiation/save/${caseUUID}`), {
                method: "POST",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
                body: JSON.stringify({
                    data: {
                        id: caseUUID,
                        type: "case",
                        attributes: attributes,
                    },
                }),
            });
            await handleJsonApiError(response);
        },
        onSuccess: async () => {
            // Invalidate negotiations queries
            await queryClient.invalidateQueries({
                queryKey: ["negotiations", caseUUID],
            });
            // This helper now invalidates both UUID and numeric ID case queries
            await invalidateCaseQueries(queryClient, caseUUID);
        },
    });
};
